import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { PrimaryButton, SecondaryButton } from "../../UI";
import PrimaryModal from "../../UI/Modal/PrimaryModal";
import AgGridWrapper from "../../UI/AgGrid";
import { ActivityBadge, Field, InfoTip, IssueList, optionsTip, OutcomeBadge, outcomesTip, Section, SelectInput, Stat, StatusMessage, WithInfo } from "./common";
import { GLOSSARY } from "./glossary";
import { compareCells, displayValue, downloadBlob, formatBytes, formatDate } from "./helpers";

const SOURCE_STATUS_STYLES = {
    ok: "bg-green-100 text-green-800",
    skipped: "bg-gray-100 text-gray-700",
    failed: "bg-red-100 text-red-700",
};

// Explains the source status badges and the table's columns.
export const SOURCE_STATS_TIP = (
    <div className="flex flex-col gap-2">
        {optionsTip("Status:", GLOSSARY.sourceStatus)}
        {optionsTip("Columns:", { calls: GLOSSARY.stats.calls, pages: GLOSSARY.stats.pages, orphans: GLOSSARY.stats.orphans, duplicates: GLOSSARY.stats.duplicates }, { calls: "Calls", pages: "Pages", orphans: "Orphans", duplicates: "Duplicates" })}
    </div>
);

const VARIANTS_TIP = optionsTip(undefined, { representative: GLOSSARY.representativeVariant, price: GLOSSARY.priceVariant }, { representative: "Representative variant", price: "Price variant" });

export const SourceStatsTable = ({ sources = {} }) => (
    <div className="overflow-x-auto">
        <table className="w-full text-xs">
            <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-1 pr-2">Source</th>
                    <th className="pr-2">Status</th>
                    <th className="pr-2">Calls</th>
                    <th className="pr-2">Pages</th>
                    <th className="pr-2">Size</th>
                    <th className="pr-2">Products</th>
                    <th className="pr-2">Variants</th>
                    <th className="pr-2">Orphans</th>
                    <th className="pr-2">Duplicates</th>
                    <th>Notes</th>
                </tr>
            </thead>
            <tbody>
                {Object.entries(sources).map(([id, source]) => {
                    const stats = source.stats || {};
                    const typeErrors = Object.entries(stats.type_errors || {}).map(([k, v]) => `${k}: ${v} not converted`);
                    const notes = [
                        source.error?.message,
                        stats.limited && "stopped at the preview limit",
                        stats.missing_product_key && `${stats.missing_product_key} without product key`,
                        stats.missing_variant_key && `${stats.missing_variant_key} variants without key`,
                        stats.filtered && `${stats.filtered} filtered`,
                        stats.failed_calls && `${stats.failed_calls} dependent ${stats.failed_calls === 1 ? "call" : "calls"} failed and ${stats.failed_calls === 1 ? "was" : "were"} skipped${stats.failed?.length ? ` (${stats.failed.map((f) => `${f.value}: ${f.message}`).join("; ")})` : ""}`,
                        stats.page_limit_reached && "page limit reached",
                        stats.truncated && "call limit reached",
                        ...typeErrors,
                    ].filter(Boolean);
                    return (
                        <tr key={id} className="border-b border-gray-100 align-top">
                            <td className="py-1 pr-2 font-mono">{id}</td>
                            <td className="pr-2">
                                <span className={`inline-block px-2 py-0.5 rounded-full font-medium ${SOURCE_STATUS_STYLES[source.status] || "bg-gray-100 text-gray-600"}`}>{source.status}</span>
                            </td>
                            <td className="pr-2">{stats.calls ?? "—"}</td>
                            <td className="pr-2">{stats.pages ?? "—"}</td>
                            <td className="pr-2">{formatBytes(stats.bytes)}</td>
                            <td className="pr-2">{stats.products ?? "—"}</td>
                            <td className="pr-2">{stats.variants ?? "—"}</td>
                            <td className="pr-2">{stats.orphans ?? "—"}</td>
                            <td className="pr-2">{(stats.duplicate_products || 0) + (stats.duplicate_variants || 0) || "—"}</td>
                            <td className="text-gray-600">{notes.join(" · ")}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    </div>
);

const limitLabel = (limit) => (limit ? `First ${limit} products` : "All products");

// Preview tab: builds the saved draft from live calls without publishing it.
// The editor owns the preview (usePreview), so it survives switching tabs but
// not closing the editor.
const PreviewTab = ({ feed, meta, dirty, saveDraft, preview }) => {
    const { job, result, draftChanged, validation, starting, building, status, build } = preview;
    const limits = meta?.preview_product_limits || [50, 200, 0];
    const [productLimit, setProductLimit] = useState(limits[0]);
    const [selected, setSelected] = useState(null);
    const [showCsv, setShowCsv] = useState(false);

    const columnDefs = useMemo(
        () => (result?.header || []).map((name) => ({
            headerName: `entity.${name}`,
            field: name,
            valueGetter: ({ data }) => data.values?.[name],
            comparator: compareCells,
            minWidth: 140,
            tooltipValueGetter: ({ value }) => value,
        })),
        [result]
    );

    const rows = result?.preview || [];

    return (
        <div className="flex flex-col gap-4 mt-4 relative">
            <div className="flex flex-wrap items-end gap-4">
                <Field label="Products to preview" tip={GLOSSARY.stats.previewLimit}>
                    <SelectInput
                        value={productLimit}
                        disabled={building}
                        options={limits.map((limit) => ({ value: limit, label: limitLabel(limit) }))}
                        onChange={(value) => setProductLimit(Number(value))}
                    />
                </Field>
                <PrimaryButton size="sm" loading={starting} disable={building} handleClick={() => build({ dirty, saveDraft, productLimit })}>{dirty ? "Save and preview" : "Build preview"}</PrimaryButton>
            </div>
            <StatusMessage status={status} />
            {building && (
                <div className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                        <ActivityBadge activity={{ stage: "building" }} />
                        <span>
                            Calling each source ({limitLabel(job.product_limit).toLowerCase()}), started {formatDate(job.started_at)}. Large feeds can take a few minutes.
                        </span>
                    </div>
                    <p className="text-sm text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2">
                        Please keep this editor open while the preview builds. Closing it or leaving the page discards the result. You can switch tabs in the editor.
                    </p>
                </div>
            )}
            {!building && result && job?.finished_at && (
                <p className="text-xs text-gray-500">
                    Preview built {formatDate(job.finished_at)}.
                    {draftChanged && <span className="text-yellow-700"> The draft has changed since; build again to see the changes.</span>}
                    {result.limited && <span> Only the first {result.product_limit} products of each primary source were read, so counts and issues cover those; a run covers the whole catalogue.</span>}
                </p>
            )}

            <IssueList title={validation?.errors?.length ? "Fix these before previewing" : undefined} errors={validation?.errors} warnings={validation?.warnings} />

            {result && (
                <>
                    <div className="flex flex-wrap items-center gap-2">
                        <Stat label="Would be" value={<OutcomeBadge outcome={result.outcome} />} tip={<div className="flex flex-col gap-1"><p>{GLOSSARY.stats.wouldBe}</p>{outcomesTip()}</div>} />
                        <Stat label="Products" value={result.rows?.live} tip={GLOSSARY.stats.products} />
                        <Stat label="Invalid rows" value={result.rows?.invalid} tip={GLOSSARY.stats.invalid} />
                        <Stat label="Columns" value={result.columns?.count} tip={GLOSSARY.stats.columns} />
                        <Stat label="File size" value={formatBytes(result.file?.bytes)} tip={GLOSSARY.stats.fileSize} />
                    </div>

                    {(result.messages || []).map((m, i) => (
                        <p key={i} className={`text-sm ${m.level === "error" ? "text-red-600" : "text-yellow-700"}`}>{m.message}</p>
                    ))}

                    {result.guardrails && (
                        <Section title="Guardrails" tip={GLOSSARY.stats.guardrails}>
                            {result.guardrails.skipped && <p className="text-sm text-gray-600">Not checked on a limited preview. Preview all products to check them.</p>}
                            {result.guardrails.checks.map((check) => (
                                <p key={check.name} className={`text-sm ${check.ok ? "text-gray-600" : "text-red-600"}`}>{check.ok ? "✓" : "✗"} {check.detail}</p>
                            ))}
                        </Section>
                    )}

                    <Section title="Sources" tip={SOURCE_STATS_TIP}>
                        <SourceStatsTable sources={result.sources} />
                        {result.merge?.unmatched && Object.keys(result.merge.unmatched).length > 0 && (
                            <p className="text-xs text-yellow-700">
                                <WithInfo tip={GLOSSARY.stats.unmatched}>Unmatched enrichment records</WithInfo>: {Object.entries(result.merge.unmatched).map(([k, v]) => `${k} ${v}`).join(", ")}
                            </p>
                        )}
                    </Section>

                    {((result.issues || []).length > 0 || (result.invalid || []).length > 0) && (
                        <Section title="Issues" tip={GLOSSARY.stats.issues}>
                            {(result.issues || []).map((issue, i) => (
                                <p key={i} className="text-sm text-yellow-700">{issue.message} <span className="text-gray-500">({issue.count} rows)</span></p>
                            ))}
                            {(result.invalid || []).map((row, i) => (
                                <p key={`inv${i}`} className="text-sm text-red-600"><span className="font-mono">{row.key}</span> dropped: {row.reason}</p>
                            ))}
                        </Section>
                    )}

                    <Section
                        title="Rows"
                        description={`First ${rows.length} rows. Click a row to see its variants and where each value came from.`}
                        tip={GLOSSARY.stats.rows}
                        actions={
                            <>
                                <SecondaryButton size="xs" handleClick={() => setShowCsv(!showCsv)}>{showCsv ? "Hide CSV" : "Show CSV"}</SecondaryButton>
                                <SecondaryButton
                                    size="xs"
                                    handleClick={() => {
                                        downloadBlob(new Blob([result.csv], { type: "text/csv;charset=utf-8" }), `${feed.name || "product-feed"}-preview.csv`);
                                        toast.success("Preview CSV downloaded");
                                    }}
                                >
                                    Download
                                </SecondaryButton>
                            </>
                        }
                    >
                        {showCsv && (
                            <textarea readOnly className="w-full h-72 font-mono text-xs border border-gray-200 rounded-md p-2 whitespace-pre" value={result.csv_truncated ? `${result.csv}\n… (truncated)` : result.csv} />
                        )}
                        <div className="ag-theme-quartz" style={{ width: "100%" }}>
                            <AgGridWrapper
                                tableId="product-feed-preview"
                                rowData={rows}
                                columnDefs={columnDefs}
                                defaultColDef={{ resizable: true, sortable: true, filter: true }}
                                onRowClicked={({ data }) => setSelected(data)}
                                tooltipShowDelay={300}
                            />
                        </div>
                    </Section>
                </>
            )}

            {selected && (
                <PrimaryModal title={`entity.id ${selected.id}`} hideModal={() => setSelected(null)} widthClass="max-w-6xl">
                    <div className="p-6 flex flex-col gap-4 max-h-[75vh] overflow-y-auto">
                        <p className="text-xs text-gray-500">
                            Sources: {(selected.sources || []).join(", ")} · representative variant: <span className="font-mono">{selected.representative || "—"}</span> · price
                            variant: <span className="font-mono">{selected.price_variant || "—"}</span> <InfoTip content={VARIANTS_TIP} wide />
                        </p>
                        <Section title="Product fields and provenance" tip={GLOSSARY.stats.provenance}>
                            <table className="text-xs w-full">
                                <tbody>
                                    {Object.entries(selected.provenance || {}).map(([field, sources]) => (
                                        <tr key={field} className="border-b border-gray-100">
                                            <td className="py-1 pr-2 font-mono">{field}</td>
                                            <td className="py-1 text-gray-600">{sources.join(", ")}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Section>
                        <Section title={`Variants (${selected.variant_count})`} tip={GLOSSARY.stats.variantsTable}>
                            <div className="overflow-x-auto">
                                <table className="text-xs w-full">
                                    <thead>
                                        <tr className="text-left text-gray-500 border-b border-gray-200">
                                            <th className="py-1 pr-2">Key</th>
                                            {[...new Set((selected.variants || []).flatMap((v) => Object.keys(v.fields)))].map((field) => <th key={field} className="pr-2">{field}</th>)}
                                            <th>Sources</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(selected.variants || []).map((variant) => (
                                            <tr key={variant.key} className="border-b border-gray-100 align-top">
                                                <td className="py-1 pr-2 font-mono">{variant.key}</td>
                                                {[...new Set((selected.variants || []).flatMap((v) => Object.keys(v.fields)))].map((field) => (
                                                    <td key={field} className="pr-2 max-w-[200px] truncate" title={`${displayValue(variant.fields[field])}${variant.provenance?.[field] ? ` (from ${variant.provenance[field].join(", ")})` : ""}`}>
                                                        {displayValue(variant.fields[field])}
                                                    </td>
                                                ))}
                                                <td>{(variant.sources || []).join(", ")}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Section>
                        <Section title="CSV values" tip={GLOSSARY.stats.csvValues}>
                            <table className="text-xs w-full">
                                <tbody>
                                    {Object.entries(selected.values || {}).map(([name, value]) => (
                                        <tr key={name} className="border-b border-gray-100 align-top">
                                            <td className="py-1 pr-2 font-mono whitespace-nowrap">entity.{name}</td>
                                            <td className="py-1 font-mono break-all">{value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Section>
                    </div>
                </PrimaryModal>
            )}
        </div>
    );
};

export default PreviewTab;
